import ko from 'knockout';
import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import styled from '@emotion/styled';

import api from '../classes/api';
import SectionBase from '../classes/sectionbase';
import Router from '../utils/router';
import { load } from '../utils/localStorage';
import { Alert, Button, Field, Input } from '../../../main/scripts/lumen';
import { useDocumentTitle } from '../../../main/scripts/utils/hooks';
import { createPasswordValidationErrors } from '../../../main/scripts/utils/password';
import { AuthCard, AuthHeading } from './login/AuthCard';

export default class JoinSection extends SectionBase {
  sectionRequiresAuth = false;

  constructor() {
    super();
    ReactDOM.render(<JoinPage />, document.getElementById('sections'));
    ko.applyBindings(this);
  }
}

export const JoinPage = () => {
  useDocumentTitle('Set up your account | Cemantica Insights hub');

  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordEnabled, setPasswordEnabled] = useState(false);
  const [errors, setErrors] = useState({});
  const [errorMessage, setErrorMessage] = useState('');
  const [needsAccount, setNeedsAccount] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const nameRef = useRef();

  const token = Router.param('token');

  useEffect(() => {
    if (!token) {
      setErrorMessage('This workspace invitation link is not valid.');
      return;
    }

    // Someone following the link may already have an account, in which case the
    // server joins them to the workspace outright and there is no form to show.
    api.join_existing_user(token, response => {
      if (response.error === 'CREATE_USER') {
        api.get_status(({ password_authentication }) => {
          setEmail(response.username);
          setPasswordEnabled(password_authentication);
          setNeedsAccount(true);
          nameRef.current?.focus();
        });
      } else {
        finish(response);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const finish = response => {
    if (response.error) {
      // An invalid password is the likeliest cause, but either way there is
      // nowhere more useful to send them than back to the form.
      setErrorMessage(
        response.message || 'There was an error joining the workspace.'
      );
      return;
    }

    const loggedInAsSomeoneElse =
      load('auth_token') != null && load('username', '') !== response;
    alert(
      loggedInAsSomeoneElse
        ? `Workspace joined!\n\nYou are currently logged in as ${load(
            'username',
            ''
          )}, but if you'd like, you can log out and log in as ${response}.`
        : 'Workspace joined!'
    );
    Router.go('index.html');
  };

  const onSubmit = event => {
    event.preventDefault();

    // There can't be password errors when passwords are disabled.
    const validationErrors = passwordEnabled
      ? createPasswordValidationErrors(password, confirmPassword, email)
      : {};
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    setErrorMessage('');
    setSubmitting(true);
    api.join_new_user(token, fullName, passwordEnabled ? password : null, response => {
      setSubmitting(false);
      finish(response);
    });
  };

  return (
    <AuthCard>
      <AuthHeading>Set up your account</AuthHeading>
      <Intro>
        Your email address will serve as your username. It cannot be changed.
      </Intro>
      {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}
      {needsAccount && (
        <Form onSubmit={onSubmit}>
          <Field label="Email" htmlFor="join-email">
            <Input
              id="join-email"
              type="text"
              autoComplete="username"
              value={email}
              readOnly
              disabled
            />
          </Field>
          <Field label="Full name" htmlFor="join-name">
            <Input
              id="join-name"
              ref={nameRef}
              type="text"
              autoComplete="name"
              value={fullName}
              onChange={event => setFullName(event.target.value)}
            />
          </Field>
          {passwordEnabled && (
            <>
              <Field
                label="Password"
                htmlFor="join-password"
                error={errors.newPass?.join('\n')}
              >
                <Input
                  id="join-password"
                  type="password"
                  autoComplete="new-password"
                  error={errors.newPass?.length > 0}
                  value={password}
                  onChange={event => setPassword(event.target.value)}
                />
              </Field>
              <Field
                label="Confirm password"
                htmlFor="join-confirm-password"
                error={errors.confirmPass?.join('\n')}
              >
                <Input
                  id="join-confirm-password"
                  type="password"
                  autoComplete="new-password"
                  error={errors.confirmPass?.length > 0}
                  value={confirmPassword}
                  onChange={event => setConfirmPassword(event.target.value)}
                />
              </Field>
            </>
          )}
          <Button
            type="submit"
            variant="primary"
            size="md"
            disabled={submitting}
            aria-busy={submitting}
            style={{ width: '100%' }}
          >
            {submitting ? 'Creating account…' : 'Create account'}
          </Button>
        </Form>
      )}
    </AuthCard>
  );
};

const Intro = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.base};
  color: ${({ theme }) => theme.color.fg.muted};
  text-align: center;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`;
